/**
 * Bond Mentor — Money Flows & Liquidity Data (Drop 5)
 *
 * Tracks institutional money flows (Treasury ETFs, TIPS, munis),
 * repo market health, Treasury market liquidity, and a composite
 * "Financial Plumbing Health" score.
 */

// ============================================
// LIQUIDITY SCORES
// ============================================
export const LIQUIDITY_LEVELS = {
  AMPLE: { label: 'Ample', color: '#22c55e', emoji: '🟢', meaning: 'Financial plumbing is healthy. Money moves freely through the system. No stress.' },
  ADEQUATE: { label: 'Adequate', color: '#3b82f6', emoji: '🔵', meaning: 'Liquidity is sufficient. Normal functioning. No concerns.' },
  THINNING: { label: 'Thinning', color: '#eab308', emoji: '🟡', meaning: 'Liquidity is starting to dry up. Larger trades are harder to execute without moving prices. Early warning.' },
  STRESSED: { label: 'Stressed', color: '#f97316', emoji: '🟠', meaning: 'Significant liquidity problems. Market-makers pulling back. Large bid-ask spreads. Prices becoming unreliable.' },
  CRISIS: { label: 'Crisis', color: '#ef4444', emoji: '🔴', meaning: 'Liquidity has evaporated. Markets are seizing up. This is when the Fed typically intervenes. Cascading effects into all asset classes.' },
};

// ============================================
// FLOW DIRECTION TYPES
// ============================================
export const FLOW_DIRS = {
  STRONG_INFLOW: { label: 'Strong Inflow', color: '#22c55e', arrow: '⬆️' },
  MILD_INFLOW: { label: 'Mild Inflow', color: '#a3e635', arrow: '↗️' },
  NEUTRAL: { label: 'Neutral', color: '#3b82f6', arrow: '➡️' },
  MILD_OUTFLOW: { label: 'Mild Outflow', color: '#eab308', arrow: '↘️' },
  STRONG_OUTFLOW: { label: 'Strong Outflow', color: '#ef4444', arrow: '⬇️' },
};

// ============================================
// MONEY FLOW DATA PER SCENARIO
// ============================================
export const MONEY_FLOW_SCENARIOS = {
  ALL_CLEAR: {
    // Treasury Flows (ETF/fund inflows vs outflows)
    treasuryFlows: {
      direction: 'NEUTRAL',
      weeklyNet: '+$1.2B',
      monthlyNet: '-$3.5B',
      interpretation: 'RISK_ON',
      note: 'Treasury fund flows are roughly flat with a slight positive tilt this week. Investors are not rushing to safety. Money is flowing into risk assets (stocks, corporate bonds) rather than safe-haven Treasuries. This is normal in a healthy market.',
    },

    // TIPS (Inflation-Protected Bonds) Demand
    tipsFlows: {
      direction: 'MILD_OUTFLOW',
      weeklyNet: '-$0.8B',
      monthlyNet: '-$4.2B',
      breakeven: 2.25,
      breakevenTrend: 'STABLE',
      breakevenColor: '#3b82f6',
      note: 'TIPS are seeing mild outflows. The breakeven inflation rate at 2.25% is stable and near the Fed target, meaning the market expects inflation to be well-controlled. No inflation scare.',
    },

    // Municipal Bond Stress
    muniStress: {
      level: 'LOW',
      color: '#22c55e',
      spreadToTreasury: 45,
      spreadTrend: 'STABLE',
      flowDirection: 'MILD_INFLOW',
      note: 'Municipal bonds are healthy. Spreads to Treasuries are tight at 45 bps, and fund flows are mildly positive. State and local government credit quality is solid. No systemic stress signal from munis.',
    },

    // Repo Market
    repo: {
      overnightRate: 4.32,
      fedTarget: 4.33,
      spread: -1,
      spreadColor: '#22c55e',
      status: 'NORMAL',
      statusColor: '#22c55e',
      note: 'Repo market is functioning perfectly. The overnight rate is right at the Fed target. Banks and institutions can borrow and lend overnight without any friction. Financial plumbing is clear.',
    },

    // Treasury Market Liquidity
    treasuryLiquidity: {
      level: 'AMPLE',
      bidAskSpread: 0.3,
      bidAskTrend: 'STABLE',
      marketDepth: 'DEEP',
      note: 'Treasury market liquidity is excellent. Bid-ask spreads are tight at 0.3 basis points — the tightest they have been in months. Market depth is strong, meaning large orders can be filled without moving prices. The world\'s safest market is functioning as it should.',
    },

    // International Capital Flows
    intlFlows: {
      direction: 'MILD_INFLOW',
      color: '#22c55e',
      note: 'Foreign capital flowing into U.S. assets. The dollar is attracting investment from abroad. This is supportive for both U.S. bonds and stocks.',
    },

    // Composite Liquidity Score
    composite: {
      score: 88,
      level: 'AMPLE',
      trend: 'STABLE',
      note: 'Overall financial system liquidity is excellent. All plumbing metrics are green. Repo market healthy, Treasury liquidity deep, no stress in any corner of the fixed-income market. This is the ideal environment for risk-taking.',
    },

    analysis: 'Money flows paint a calm picture. Investors are positioned for growth — flowing into risk assets and out of safe havens. Inflation expectations are anchored at 2.25%. The repo market and Treasury liquidity are both in excellent shape. Municipal bonds are healthy. Financial system plumbing score: 88/100 (Ample). There is no liquidity concern from any angle.',
  },

  EARLY_WARNING: {
    treasuryFlows: {
      direction: 'MILD_INFLOW',
      weeklyNet: '+$6.8B',
      monthlyNet: '+$18.5B',
      interpretation: 'FLIGHT_STARTING',
      note: 'Treasury fund inflows are accelerating. Institutional investors are rotating from stocks into the safety of government bonds. $18.5B in monthly net inflows is significantly above normal. Smart money is quietly de-risking.',
    },

    tipsFlows: {
      direction: 'STRONG_INFLOW',
      weeklyNet: '+$3.2B',
      monthlyNet: '+$12.8B',
      breakeven: 2.65,
      breakevenTrend: 'RISING',
      breakevenColor: '#f97316',
      note: 'TIPS demand is surging. Breakeven inflation has jumped to 2.65% — well above the Fed\'s 2% target. Smart money is betting that inflation will run hotter than the Fed admits. This is a meaningful signal — when TIPS demand spikes, it means institutional investors are hedging against inflation risk.',
    },

    muniStress: {
      level: 'MODERATE',
      color: '#eab308',
      spreadToTreasury: 78,
      spreadTrend: 'WIDENING',
      flowDirection: 'MILD_OUTFLOW',
      note: 'Municipal bonds are showing early stress. Spreads have widened to 78 bps from 45 bps last month, and fund outflows have started. When munis weaken independently, it often signals broader liquidity tightening in the financial system.',
    },

    repo: {
      overnightRate: 4.48,
      fedTarget: 4.33,
      spread: 15,
      spreadColor: '#eab308',
      status: 'ELEVATED',
      statusColor: '#eab308',
      note: 'Repo rate is running 15 basis points ABOVE the Fed target. This is a yellow flag — it means demand for overnight borrowing exceeds the available supply of funds. Banks are hoarding cash slightly. Not yet alarming, but the elevated rate signals tightening conditions under the surface.',
    },

    treasuryLiquidity: {
      level: 'THINNING',
      bidAskSpread: 0.8,
      bidAskTrend: 'WIDENING',
      marketDepth: 'MODERATE',
      note: 'Treasury liquidity is deteriorating. Bid-ask spreads have more than doubled to 0.8 bps. Market depth has thinned — large trades are starting to move prices. This is an important early indicator that even the safest market in the world is experiencing friction.',
    },

    intlFlows: {
      direction: 'NEUTRAL',
      color: '#eab308',
      note: 'International flows have stalled. Foreign investors are neither adding to nor selling U.S. assets. This pause often precedes outflows if conditions deteriorate further.',
    },

    composite: {
      score: 52,
      level: 'THINNING',
      trend: 'DETERIORATING',
      note: 'Financial plumbing health has degraded to "Thinning." Multiple indicators are yellow: repo rates elevated, Treasury liquidity worsening, muni stress appearing. The system still functions but is under strain. This level historically precedes further deterioration if the underlying causes are not addressed.',
    },

    analysis: 'Money flows reveal increasing fear beneath the surface. Institutional money is rotating into Treasuries at $18.5B/month — classic flight-to-safety behavior. TIPS demand is surging with breakeven inflation at 2.65%, meaning smart money expects inflation to persist. The repo market is showing strain at 15bps above target. Treasury liquidity has thinned meaningfully. Municipal bonds are weakening. Composite liquidity score: 52/100 (Thinning). The financial system plumbing is not broken, but it is creaking. Watch the repo rate and Treasury bid-ask spreads closely — if they worsen, the entire system could seize up quickly.',
  },

  DANGER_ZONE: {
    treasuryFlows: {
      direction: 'STRONG_INFLOW',
      weeklyNet: '+$28.5B',
      monthlyNet: '+$85.0B',
      interpretation: 'PANIC_FLIGHT',
      note: 'MASSIVE flight to safety. $85 billion monthly net inflows into Treasury funds — panic-level demand for government bonds. Institutional investors are liquidating stocks, corporate bonds, and other risk assets and piling into Treasuries at a pace not seen since 2020. This is not cautious repositioning. This is fear.',
    },

    tipsFlows: {
      direction: 'STRONG_OUTFLOW',
      weeklyNet: '-$5.5B',
      monthlyNet: '-$18.0B',
      breakeven: 3.15,
      breakevenTrend: 'SPIKING',
      breakevenColor: '#ef4444',
      note: 'Paradoxically, TIPS are now seeing OUTFLOWS even as inflation expectations spike to 3.15%. This signals a liquidity crisis — investors are selling everything, even inflation protection, because they need CASH. When TIPS outflows happen alongside rising breakevens, it means the market is in "sell everything" mode.',
    },

    muniStress: {
      level: 'SEVERE',
      color: '#ef4444',
      spreadToTreasury: 165,
      spreadTrend: 'BLOWING_OUT',
      flowDirection: 'STRONG_OUTFLOW',
      note: 'Municipal bonds are in crisis. Spreads have exploded to 165 bps — more than triple the calm-market level. Fund outflows are accelerating. State and local governments are seeing their borrowing costs spike. When munis break down this badly, it signals systemic liquidity failure. This is what happened in March 2020 before the Fed intervened.',
    },

    repo: {
      overnightRate: 5.85,
      fedTarget: 5.08,
      spread: 77,
      spreadColor: '#ef4444',
      status: 'CRISIS',
      statusColor: '#ef4444',
      note: 'At this level, the repo market is showing crisis-like stress: the overnight rate here sits about 77 basis points above the Fed target — a large dislocation that would mean banks are hoarding cash and interbank lending is under severe strain. For context, a smaller repo spike in September 2019 prompted the Fed to step in quickly. This is an illustrative example of what a severe reading looks like, not a live confirmation.',
    },

    treasuryLiquidity: {
      level: 'CRISIS',
      bidAskSpread: 3.2,
      bidAskTrend: 'BLOWN_OUT',
      marketDepth: 'EVAPORATED',
      note: 'At this level, Treasury liquidity would be severely impaired: bid-ask spreads around 3.2 bps are more than 10x normal, meaning even U.S. government bonds are hard to trade efficiently and market-makers have pulled back. Readings this extreme have historically appeared only in the most severe episodes (2008, March 2020), which are the kinds of conditions where the Fed has stepped in. This illustrates a worst-case regime rather than a live confirmation.',
    },

    intlFlows: {
      direction: 'STRONG_OUTFLOW',
      color: '#ef4444',
      note: 'Foreign capital is fleeing U.S. assets. International investors are repatriating funds. The dollar may actually weaken despite the flight to safety because foreign holders are liquidating U.S. bonds. This adds selling pressure on top of an already stressed market.',
    },

    composite: {
      score: 15,
      level: 'CRISIS',
      trend: 'COLLAPSING',
      note: 'This illustrates a severe-stress regime (score 15/100): repo strain, thin Treasury liquidity, weak munis, and "sell everything" behavior all showing at once. Historically, environments like this are where the Fed has launched emergency lending or resumed asset purchases. At readings this low, investors have historically leaned heavily toward capital preservation. Shown as an educational example of the regime — confirm against a live liquidity feed before treating it as current.',
    },

    analysis: 'This illustrates what a full liquidity-crisis regime looks like. In an episode like this, money flows show panic: heavy flight into Treasuries, yet TIPS sold for cash even as inflation rises — the hallmark of "sell everything" liquidation. Repo sits far above target, Treasury bid-ask spreads widen so much that even government bonds trade poorly, munis blow out, and international capital leaves. Composite liquidity score at this level: 15/100 (Crisis). In past episodes this severe (2008, March 2020), the Fed launched emergency programs within days. At readings like this, investors have historically prioritized liquidity and capital preservation, leaning toward cash and ultra-short Treasury bills. This is educational context for the regime — not a live signal or a recommendation. Always confirm against live data and consult a qualified advisor.',
  },
};
